import { Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AdvisoryTabService } from '../advisory-tab.service';
import { WaterBodySelectionService } from '../waterbody-selection.service';
import { WaterbodiesService } from '../waterbodies/waterbodies.service';
import { ProfileService } from '../profile.service';
import { APP_CONFIG, AppConfig } from '../models/app-config';

// Type-only imports — provide TypeScript types without adding to the bundle.
// The runtime values are loaded lazily inside initializeMap() and populateOutfallGraphics().
import type WebMap from '@arcgis/core/WebMap';
import type MapView from '@arcgis/core/views/MapView';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import type GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import type Extent from '@arcgis/core/geometry/Extent';

@Component({
    selector: 'jhi-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.css'],
    standalone: true,
    imports: []
})
export class MapComponent implements OnInit, OnDestroy {
  public webmap?: WebMap;
  public view?: MapView;
  private csoAdvisoryLayer: FeatureLayer | null = null;
  private wqAdvisoryLayer: FeatureLayer | null = null;
  private outfallsLayer: FeatureLayer | null = null;
  private outfallsGraphicsLayer: GraphicsLayer | null = null;
  private tabSubscription: Subscription | null = null;
  private selectionSubscription: Subscription | null = null;
  private ExtentCtor?: new (...args: any[]) => Extent;

  @Output() onSketchUpdated = new EventEmitter<any>();

  constructor(
    private advisoryTabService: AdvisoryTabService,
    private selectionService: WaterBodySelectionService,
    private waterbodiesService: WaterbodiesService,
    private profileService: ProfileService,
    @Inject(APP_CONFIG) private config: AppConfig
  ) {}

  @ViewChild('mapViewNode', { static: true }) private mapViewEl!: ElementRef;

  async initializeMap(): Promise<any> {
    const [
      { default: WebMap },
      { default: MapView },
      { default: GraphicsLayer },
      { default: Compass },
      { default: Extent },
      { createCsoAdvisoryLayer },
      { createWqAdvisoryLayer },
      { createOutfallsLayer },
    ] = await Promise.all([
      import('@arcgis/core/WebMap'),
      import('@arcgis/core/views/MapView'),
      import('@arcgis/core/layers/GraphicsLayer'),
      import('@arcgis/core/widgets/Compass'),
      import('@arcgis/core/geometry/Extent'),
      import('./layers/cso-advisory.layer'),
      import('./layers/wq-advisory.layer'),
      import('./layers/outfalls.layer'),
    ]);

    this.ExtentCtor = Extent;

    const container = this.mapViewEl.nativeElement;
    this.webmap = new WebMap({ basemap: 'topo' });

    if (this.config.showAdvisories.includes('cso')) {
      this.csoAdvisoryLayer = createCsoAdvisoryLayer(this.profileService.get('cso.arcgis.feature_layer.url'));
      this.webmap.add(this.csoAdvisoryLayer);
      this.outfallsLayer = createOutfallsLayer();
      this.webmap.add(this.outfallsLayer);
      this.outfallsGraphicsLayer = new GraphicsLayer({ visible: false });
      this.webmap.add(this.outfallsGraphicsLayer);
    }

    if (this.config.showAdvisories.includes('wq')) {
      this.wqAdvisoryLayer = createWqAdvisoryLayer(this.profileService.get('wq.arcgis.feature_layer.url'));
      this.webmap.add(this.wqAdvisoryLayer);
    }

    this.view = new MapView({
      container,
      map: this.webmap,
      popup: {
        dockEnabled: true,
        dockOptions: { buttonEnabled: false, position: 'bottom-right' }
      },
      constraints: { rotationEnabled: false },
      center: [-74.0060, 40.7128],
      zoom: 11,
    });

    this.view.ui.add(new Compass({}), 'bottom-right');
    return this.view.when();
  }

  ngOnInit(): void {
    this.initializeMap().then(() => {
      console.log('The map is ready.');

      this.tabSubscription = this.advisoryTabService.activeTab$.subscribe(tab => {
        this.applyTabVisibility(tab);
      });

      this.selectionSubscription = this.selectionService.selectedWaterBody$.subscribe(wb => {
        if (wb && this.view) {
          this.waterbodiesService.getExtent(wb).subscribe({
            next: response => {
              if (response.extent && this.view && this.ExtentCtor) {
                this.view.goTo(new this.ExtentCtor(response.extent));
              }
            },
            error: err => console.error('Error fetching waterbody extent:', err)
          });
        } else if (this.view) {
          this.view.goTo({ center: [-74.0060, 40.7128], zoom: 11 });
        }
      });

      this.populateOutfallGraphics();
    });
  }

  private applyTabVisibility(tab: 'wq' | 'cso'): void {
    if (this.wqAdvisoryLayer) this.wqAdvisoryLayer.visible = tab === 'wq';
    if (this.csoAdvisoryLayer) this.csoAdvisoryLayer.visible = tab === 'cso';
    if (this.outfallsLayer) this.outfallsLayer.visible = false;
    if (this.outfallsGraphicsLayer) this.outfallsGraphicsLayer.visible = tab === 'cso';
  }

  private async populateOutfallGraphics(): Promise<void> {
    if (!this.outfallsLayer || !this.csoAdvisoryLayer || !this.outfallsGraphicsLayer) return;

    const [
      { default: Graphic },
      { distance },
      { default: SimpleMarkerSymbol },
      { default: PopupTemplate },
    ] = await Promise.all([
      import('@arcgis/core/Graphic'),
      import('@arcgis/core/geometry/geometryEngine'),
      import('@arcgis/core/symbols/SimpleMarkerSymbol'),
      import('@arcgis/core/PopupTemplate'),
    ]);

    const makeSymbol = (color: string, size: number): SimpleMarkerSymbol =>
      new SimpleMarkerSymbol({ style: 'circle', color, outline: { color: 'white', width: 1 }, size });

    const symbolForVolume = (volume: number): SimpleMarkerSymbol => {
      if (volume === 0)    return makeSymbol('rgba(0, 0, 255, 0.5)', 8);
      if (volume <= 5)     return makeSymbol('#006400', 10);
      if (volume <= 20)    return makeSymbol('#90EE90', 12);
      if (volume <= 50)    return makeSymbol('#FFFF00', 16);
      if (volume <= 200)   return makeSymbol('#FFD700', 20);
      if (volume <= 500)   return makeSymbol('#FFA500', 24);
      if (volume <= 1000)  return makeSymbol('#FFB6C1', 28);
      return makeSymbol('#8B0000', 32);
    };

    try {
      const [csoResult, outfallResult] = await Promise.all([
        this.csoAdvisoryLayer.queryFeatures({ where: '1=1', outFields: ['FID', 'Volume', 'Waterbod_1'], returnGeometry: true }),
        this.outfallsLayer.queryFeatures({ where: '1=1', outFields: ['*'], returnGeometry: true })
      ]);

      const csoFeatures = csoResult.features;
      const outfallGraphics = outfallResult.features.map(outfall => {
        let closestVolume = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        for (const cso of csoFeatures) {
          if (!outfall.geometry || !cso.geometry) continue;
          const d = distance(outfall.geometry, cso.geometry, 'meters');
          if (d < closestDistance) { closestDistance = d; closestVolume = cso.attributes?.Volume ?? 0; }
        }
        return new Graphic({
          geometry: outfall.geometry,
          symbol: symbolForVolume(closestVolume),
          attributes: { ...outfall.attributes, closestVolume },
          popupTemplate: new PopupTemplate({
            // title: '{unitid}',
            content: 'Outfall: {unitid}<br>Closest CSO volume: {closestVolume} MG'
          })
        });
      });

      this.outfallsGraphicsLayer.removeAll();
      this.outfallsGraphicsLayer.addMany(outfallGraphics);
    } catch (err) {
      console.error('Error populating outfall graphics:', err);
    }
  }

  ngOnDestroy(): void {
    if (this.view) this.view.destroy();
    this.tabSubscription?.unsubscribe();
    this.selectionSubscription?.unsubscribe();
  }
}
